import React, { useState } from 'react';
import { Play, CheckCircle, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import type { TrainingModule, Quiz, ModuleResult, QuestionResult } from '../types';
import { cn } from '../utils/cn';
import { QuizPlayer } from './QuizPlayer';
import { QuizResult } from './QuizResult';

interface ModuleViewerProps {
  module: TrainingModule;
  quiz?: Quiz;
  onComplete: (score: number, moduleResult: ModuleResult) => void;
}

function getYouTubeEmbedUrl(url: string) {
  const videoId = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})(?:\S+)?/)?.[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1` : null;
}

export function ModuleViewer({ module, quiz, onComplete }: ModuleViewerProps) {
  const [showQuiz, setShowQuiz] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizResults, setQuizResults] = useState<QuestionResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleQuizComplete = (score: number, results: QuestionResult[]) => {
    setQuizScore(score);
    setQuizResults(results);
    setShowResults(true);
  };

  const handleContinue = () => {
    onComplete(quizScore, {
      moduleId: module.id,
      score: quizScore,
      results: quizResults
    });
  };

  const handleRetry = () => {
    setShowQuiz(true);
    setShowResults(false);
  };

  if (showResults) {
    return (
      <QuizResult
        score={quizScore}
        results={quizResults}
        onRetry={quizScore < 70 ? handleRetry : undefined}
        onContinue={handleContinue}
      />
    );
  }

  if (showQuiz) {
    if (!quiz) {
      return (
        <div className="max-w-4xl mx-auto p-4">
          <div className="rounded-lg bg-red-50 p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="text-sm text-red-800">
                Este módulo não possui questionário. Por favor, entre em contato com o suporte.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <QuizPlayer
        quiz={quiz}
        userId="temp-user-id" // TODO: Get from auth context
        onComplete={handleQuizComplete}
      />
    );
  }

  const embedUrl = getYouTubeEmbedUrl(module.videoUrl);
  if (!embedUrl) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-800">
              URL do vídeo inválida. Por favor, entre em contato com o suporte.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="aspect-video w-full rounded-xl overflow-hidden bg-gray-900 shadow-lg">
        <iframe
          src={embedUrl}
          className="h-full w-full"
          title={module.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{module.title}</h2>
            <p className="mt-1 text-sm text-gray-500">
              Duração: {Math.floor(module.duration / 60)}min {module.duration % 60}s
            </p>
          </div>
          <button
            onClick={() => setShowQuiz(true)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-6 py-3",
              "text-white font-medium",
              "transition-all hover:opacity-90 active:scale-95",
              "bg-[#7c3aed]"
            )}
          >
            <Play className="h-5 w-5" />
            Iniciar Avaliação
          </button>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900">Descrição do Módulo</h3>
          <p className="mt-2 text-gray-600">{module.description}</p>
        </div>
      </div>
    </div>
  );
}